import React, { useState } from 'react';
import { Card, Table, Button, Form, Modal, Carousel, Row, Col } from 'react-bootstrap';
import '../../FancoilTable/FancoilTable.css';
import './ValveAuditTable.css';

const strip = (s) => (s || '').replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '').trim();

const conditionBadge = (val) => {
  const c = strip(val);
  if (!c) return <span className="va-dash">&mdash;</span>;
  if (c.includes('OK')) return <span className="inspection-badge badge-ok">OK</span>;
  if (c.includes('Stiff')) return <span className="inspection-badge valve-badge-stiff">Stiff</span>;
  if (c.includes('Seized')) return <span className="inspection-badge badge-needs-service">Seized</span>;
  return <span className="inspection-badge">{c}</span>;
};

const sealBadge = (val) => {
  const c = strip(val);
  if (!c) return <span className="va-dash">&mdash;</span>;
  if (c.includes('Tight')) return <span className="inspection-badge badge-ok">Tight</span>;
  if (c.includes('Passing')) return <span className="inspection-badge badge-needs-service">Passing</span>;
  if (c.includes('Not')) return <span className="inspection-badge badge-needs-service">Not Closing</span>;
  return <span className="inspection-badge">{c}</span>;
};

const replacementBadge = (val) => {
  const c = strip(val);
  if (!c) return <span className="va-dash">&mdash;</span>;
  if (c.includes('Needs')) return <span className="inspection-badge badge-needs-service">Needs Replacement</span>;
  return <span className="inspection-badge badge-ok">No</span>;
};

function ValveAuditTable({
  rows, sortedData, startIndex, endIndex,
  currentPage, totalPages, rowsPerPage,
  sortColumn, sortDirection,
  handleSort, handlePrevPage, handleNextPage, handleRowsPerPageChange,
}) {
  const [detailRow, setDetailRow] = useState(null);
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);

  const arrow = (col) => sortColumn === col ? (sortDirection === 'asc' ? ' ↑' : ' ↓') : '';

  const collectPhotos = (row) => {
    const groups = [
      { urls: row.issuePhotos || [], label: 'Issue' },
      { urls: row.valvePhotos || [], label: 'Valve' },
      { urls: row.nameplatePhotos || [], label: 'Nameplate' },
      { urls: row.newTagPhotos || [], label: 'New Tag' },
    ];
    const all = [];
    groups.forEach(g => g.urls.forEach(u => { if (u) all.push({ url: u, label: g.label }); }));
    return all;
  };

  const openCarousel = (urls, idx) => { setCarouselPhotos(urls); setCarouselIndex(idx); setShowCarousel(true); };

  const renderPhotos = (row) => {
    const photos = collectPhotos(row);
    if (!photos.length) return <span className="va-dash">&mdash;</span>;
    const toShow = photos.slice(0, 2);
    const remaining = photos.length - 2;
    return (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        {toShow.map((p, i) => (
          <img key={i} src={p.url} alt={p.label} className="file-thumbnail" loading="lazy"
            onClick={(e) => { e.stopPropagation(); openCarousel(photos.map(x => x.url), i); }} />
        ))}
        {remaining > 0 && (
          <span className="va-photo-more" onClick={(e) => { e.stopPropagation(); openCarousel(photos.map(x => x.url), 2); }}>
            +{remaining}
          </span>
        )}
      </div>
    );
  };

  const getNotes = (row) => {
    const parts = [row.valveIssueDesc, row.sealIssueDesc, row.additionalNotes].filter(Boolean);
    if (!parts.length) return null;
    return parts.join(' · ');
  };

  const field = (label, value) => {
    const v = strip(value);
    if (!v) return null;
    return (
      <div className="va-detail-field">
        <div className="va-detail-label">{label}</div>
        <div className="va-detail-value">{v}</div>
      </div>
    );
  };

  const noteItems = (r) => (
    [
      r.valveIssueDesc && { label: 'Valve Issue', text: r.valveIssueDesc },
      r.sealIssueDesc && { label: 'Seal Issue', text: r.sealIssueDesc },
      r.additionalNotes && { label: 'Notes', text: r.additionalNotes },
    ].filter(Boolean)
  );

  const renderDetailModal = () => {
    if (!detailRow) return null;
    const r = detailRow;
    const photos = collectPhotos(r);
    const notes = noteItems(r);

    const leftFields = [
      ['Location', r.locationCategory],
      ['Location Details', r.locationDetails],
      ['Technician', r.technician],
      ['Valve Size', r.valveSize ? `${strip(r.valveSize)}"` : ''],
      ['Connection', r.connectionType],
      r.connectionTypeOther ? ['Connection (Other)', r.connectionTypeOther] : null,
    ].filter(Boolean);

    const rightFields = [
      ['Potable', r.isPotable],
      ['Domestic Type', r.domesticType],
      ['Dual Temp Riser', r.isDualTemp],
      r.isDualTemp && strip(r.isDualTemp) !== 'No' ? ['Dual Temp Type', r.dualTempType] : null,
      ['Valve Servicing', r.valveServicing],
      ['Corresponding Tags', r.correspondingValveTags],
    ].filter(Boolean);

    return (
      <Modal
        show={!!detailRow}
        onHide={() => setDetailRow(null)}
        centered
        size="xl"
        dialogClassName="va-detail-modal"
      >
        <Modal.Header closeButton className="va-detail-header">
          <div>
            <Modal.Title className="va-detail-title">
              {strip(r.tagNumber) || 'Valve Detail'}
            </Modal.Title>
            {(r.visitDate || r.locationCategory) && (
              <div className="va-detail-subtitle">
                {r.visitDate && <span>{r.visitDate}</span>}
                {r.visitDate && r.locationCategory && <span className="va-detail-dot">•</span>}
                {r.locationCategory && <span>{strip(r.locationCategory)}</span>}
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body className="va-detail-body">
          {/* Status summary row */}
          <div className="va-status-row">
            <div className="va-status-item">
              <span className="va-modal-sublabel">Valve</span>
              {conditionBadge(r.valveCondition)}
            </div>
            <div className="va-status-item">
              <span className="va-modal-sublabel">Seal</span>
              {sealBadge(r.sealCondition)}
            </div>
            <div className="va-status-item">
              <span className="va-modal-sublabel">Replacement</span>
              {replacementBadge(r.replacementStatus)}
            </div>
          </div>

          {photos.length > 0 && (
            <div className="va-photos-block">
              <div className="va-photos-heading">Photos ({photos.length})</div>
              <div className="va-photos-strip">
                {photos.map((p, i) => (
                  <div
                    key={i}
                    className="va-photo-tile"
                    onClick={() => openCarousel(photos.map(x => x.url), i)}
                  >
                    <img src={p.url} alt={p.label} loading="lazy" />
                    <div className="va-photo-caption">{p.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {notes.length > 0 && (
            <div className="va-notes-block">
              <div className="va-notes-heading">Notes</div>
              {notes.map((n, i) => (
                <div key={i} className="va-note-item">
                  <span className="va-note-label">{n.label}:</span> {n.text}
                </div>
              ))}
            </div>
          )}

          {/* More details */}
          <div className="va-detail-grid-wrapper">
            <div className="va-detail-grid-heading">More Details</div>
            <Row className="va-detail-grid g-0">
              <Col md={6} className="va-detail-col">
                {leftFields.map(([l, v]) => field(l, v))}
              </Col>
              <Col md={6} className="va-detail-col">
                {rightFields.map(([l, v]) => field(l, v))}
              </Col>
            </Row>
          </div>
        </Modal.Body>
      </Modal>
    );
  };

  return (
    <div className="fancoil-table-container">
      <Card className="fancoil-table-card">
        <Card.Body>
          <Table hover responsive className="fancoil-table valve-audit-table">
            <thead>
              <tr>
                <th className="clickable-th" onClick={() => handleSort('tagNumber')}>Tag #{arrow('tagNumber')}</th>
                <th className="clickable-th" onClick={() => handleSort('locationCategory')}>Location{arrow('locationCategory')}</th>
                <th className="clickable-th">Size</th>
                <th className="clickable-th" onClick={() => handleSort('valveCondition')}>Valve{arrow('valveCondition')}</th>
                <th className="clickable-th" onClick={() => handleSort('sealCondition')}>Seal{arrow('sealCondition')}</th>
                <th className="clickable-th" onClick={() => handleSort('replacementStatus')}>Replacement{arrow('replacementStatus')}</th>
                <th className="clickable-th">Photos</th>
                <th className="clickable-th notes-column">Notes</th>
                <th className="clickable-th" style={{ width: '100px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const notes = getNotes(row);
                return (
                  <tr key={row.objectId || idx}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{strip(row.tagNumber) || '—'}</td>
                    <td className="va-loc-cell">
                      <div className="va-loc-title">{strip(row.locationCategory) || '—'}</div>
                      {row.locationDetails && <div className="va-loc-detail">{strip(row.locationDetails)}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {strip(row.valveSize) ? `${strip(row.valveSize)}"` : '—'}
                      {strip(row.connectionType) && <div style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{strip(row.connectionType)}</div>}
                    </td>
                    <td>{conditionBadge(row.valveCondition)}</td>
                    <td>{sealBadge(row.sealCondition)}</td>
                    <td>{replacementBadge(row.replacementStatus)}</td>
                    <td className="photos-cell">{renderPhotos(row)}</td>
                    <td className="notes-cell">{notes || <span className="va-dash">&mdash;</span>}</td>
                    <td>
                      <Button size="sm" className="issues-button" style={{ minWidth: '80px' }} onClick={() => setDetailRow(row)}>View</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={9}>
                  <div className="fancoil-pagination d-flex justify-content-between align-items-center mt-3">
                    <div className="d-flex align-items-center">
                      <span className="me-2">Rows per page:</span>
                      <Form.Select value={rowsPerPage} onChange={handleRowsPerPageChange} className="rows-per-page-select" style={{ width: 'auto' }}>
                        <option value="10">10</option><option value="25">25</option><option value="50">50</option>
                      </Form.Select>
                      <span className="ms-3">Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length}</span>
                    </div>
                    <div className="d-flex align-items-center" style={{ marginLeft: 'auto' }}>
                      <span className="me-3">Page {currentPage} of {totalPages}</span>
                      <Button variant="outline-secondary" size="sm" className="me-2" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</Button>
                      <Button variant="outline-secondary" size="sm" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>

      {renderDetailModal()}

      <Modal show={showCarousel} onHide={() => setShowCarousel(false)} centered size="lg">
        <Modal.Body className="p-0">
          <Carousel activeIndex={carouselIndex} onSelect={setCarouselIndex} interval={null}>
            {(carouselPhotos || []).map((url, i) => (
              <Carousel.Item key={url || i}>
                <img src={url} alt="Preview" className="d-block w-100 carousel-image" />
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default ValveAuditTable;
